if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');
        // Pre-orders never enter the cart: a cart mixing them with in-stock items
        // cannot check out (snippets/cart-preorder-mix.liquid).
        this.isolatedCheckout = this.form.classList.contains('js-isolated-checkout');

        if (theme.settings.afterAtc !== 'no-js' || this.isolatedCheckout) {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }

        if (this.isolatedCheckout) {
          // Delegated because a variant change re-renders the submit button.
          const prefetchFromSubmit = (evt) => {
            if (evt.target.closest('[name="add"]')) this.prefetchCheckout();
          };
          this.form.addEventListener('pointerover', prefetchFromSubmit);
          this.form.addEventListener('pointerdown', prefetchFromSubmit);
          this.form.addEventListener('touchstart', prefetchFromSubmit, { passive: true });
          this.form.addEventListener('change', () => { this.prefetchedCheckout = null; });
          window.addEventListener('pageshow', () => this.resetCheckoutButtons());
        }

        // Only the PDP and featured-product sections render this button; the other
        // seven sections that load this file (collection, search, recommendations,
        // recently viewed) have no Buy it now, so this is a no-op there.
        this.buyItNowBtn = this.form.querySelector('.js-buy-it-now');
        if (this.buyItNowBtn) {
          this.buyItNowBtn.addEventListener('click', this.handleBuyItNow.bind(this));
          // Three escalating chances to have the checkout already building before
          // the click lands: hover (best case, hundreds of ms), then pointerdown
          // which fires ~100ms before click and also covers a cold mobile tap.
          this.buyItNowBtn.addEventListener('pointerenter', this.prefetchCheckout.bind(this));
          this.buyItNowBtn.addEventListener('pointerdown', this.prefetchCheckout.bind(this));
          this.buyItNowBtn.addEventListener('touchstart', this.prefetchCheckout.bind(this), { passive: true });

          // A variant or quantity change invalidates any in-flight prefetch.
          this.form.addEventListener('change', () => { this.prefetchedCheckout = null; });

          // Returning via the back button restores this page from the bfcache
          // (a live in-memory snapshot, not a re-fetch), so the DOM comes back
          // exactly as it was left: mid-navigation, still spinning, and still
          // aria-disabled -- which left the button permanently dead. Reset it
          // whenever the page is shown again. `persisted` is true only for a
          // bfcache restore, but resetting unconditionally is harmless and also
          // covers a normal re-show.
          window.addEventListener('pageshow', () => this.resetCheckoutButtons());
        }
      }
    }

    /**
     * Sends the shopper straight to a checkout containing only this item.
     *
     * The Storefront API's cart is a separate object from the Liquid/Ajax cart
     * behind the `cart` cookie, and the two never sync. That separation is a
     * documented limitation for headless builds; here it is exactly the property
     * needed, because it leaves the shopper's own cart untouched. The two
     * alternatives were measured and rejected (acs-tech A79): `/cart/add` with
     * `return_to` ADDS to the cart, so repeat clicks multiply the order, and a
     * `/cart/{variant}:{qty}` permalink REPLACES the cart, destroying it.
     */
    async handleBuyItNow() {
      if (this.buyItNowBtn.getAttribute('aria-disabled') === 'true') return;

      try {
        await this.goToIsolatedCheckout(this.buyItNowBtn);
      } catch (error) {
        console.log(error); // eslint-disable-line
        // Availability over correctness: this fallback carries the whole cart, so
        // A79's symptom returns in this path. Accepted because a dead button is a
        // lost sale, and it never runs when the API is reachable.
        ProductForm.resetCheckoutButton(this.buyItNowBtn);
        ProductForm.submitToCartAndCheckout(this.form);
      }
    }

    /**
     * The pre-order submit button's handler. Unlike Buy it now there is no
     * add-to-cart fallback, since that would recreate the mixed cart this exists
     * to prevent; the shopper sees an error and can retry.
     */
    async handlePreorderCheckout() {
      this.setErrorMsgState();
      try {
        await this.goToIsolatedCheckout(this.submitBtn);
      } catch (error) {
        console.log(error); // eslint-disable-line
        ProductForm.resetCheckoutButton(this.submitBtn);
        this.setErrorMsgState(this.querySelector('.js-form-error')?.dataset.checkoutError);
      }
    }

    /**
     * Navigates to a single-item checkout for the form's variant and quantity.
     * @param {Element} button - The clicked button, shown as loading meanwhile.
     */
    async goToIsolatedCheckout(button) {
      const variantId = this.form.querySelector('[name="id"]')?.value;
      if (!variantId) return;
      const quantity = Number(this.form.querySelector('[name="quantity"]')?.value) || 1;

      button.setAttribute('aria-disabled', 'true');
      button.classList.add('is-loading');

      const checkoutUrl = this.prefetchedCheckout?.variantId === variantId
        && this.prefetchedCheckout?.quantity === quantity
        ? await this.prefetchedCheckout.promise
        : await ProductForm.createIsolatedCheckout(variantId, quantity);

      // Must be assign/href, never location.replace: replace overwrites the
      // product page in history, so Back skips it entirely. The stale-spinner
      // problem that tempted replace here is handled by the pageshow reset.
      window.location.href = checkoutUrl;
    }

    /**
     * Runs on pageshow, because the success path navigates away without ever
     * reaching a finally block, and a bfcache restore brings the button back
     * still spinning and aria-disabled.
     */
    resetCheckoutButtons() {
      this.form.querySelectorAll('.js-buy-it-now, [name="add"]').forEach((button) => {
        if (button.classList.contains('is-loading')) ProductForm.resetCheckoutButton(button);
      });
    }

    /**
     * Clears a button's loading state.
     * @param {Element} button - The button.
     */
    static resetCheckoutButton(button) {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
    }

    /**
     * Starts building the checkout on hover/touch, before the click.
     *
     * The click cannot navigate until cartCreate returns, so the shopper watches
     * a spinner for the whole round trip. Starting on pointerenter usually buys
     * a few hundred milliseconds of that back. The result is reused only if the
     * variant and quantity still match at click time, so changing either falls
     * back to a fresh request. Errors are swallowed here and rediscovered by the
     * click, which owns the fallback path.
     */
    prefetchCheckout() {
      const variantId = this.form.querySelector('[name="id"]')?.value;
      if (!variantId) return;
      const quantity = Number(this.form.querySelector('[name="quantity"]')?.value) || 1;
      if (this.prefetchedCheckout?.variantId === variantId
        && this.prefetchedCheckout?.quantity === quantity) return;

      const promise = ProductForm.createIsolatedCheckout(variantId, quantity);
      promise.catch(() => {});
      this.prefetchedCheckout = { variantId, quantity, promise };
    }

    /**
     * Builds a single-item cart via the Storefront API and returns its checkout URL.
     *
     * Tokenless: Shopify documents Storefront API access without a token for a
     * capability list that includes Cart (read/write), so no app and no token in
     * theme code.
     *
     * The pinned version is the one maintenance point. An EXPIRED version does not
     * error -- Shopify falls forward and silently serves the oldest supported
     * version (measured: /api/2019-04/ returned 200, served by 2025-10), so drift
     * would be invisible until a schema change broke the query at runtime. The
     * header comparison below turns that silent case into a visible warning about a
     * year before it can bite. A NONEXISTENT version 404s, so typos stay loud.
     * @param {string} variantId - Variant to buy.
     * @param {number} quantity - How many.
     * @returns {Promise<string>} The checkout URL.
     */
    static async createIsolatedCheckout(variantId, quantity) {
      const apiVersion = '2026-07';
      const response = await fetch(`/api/${apiVersion}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query: `mutation buyItNow($lines: [CartLineInput!]!) {
            cartCreate(input: { lines: $lines }) {
              cart { checkoutUrl }
              userErrors { message }
            }
          }`,
          variables: {
            lines: [{ merchandiseId: `gid://shopify/ProductVariant/${variantId}`, quantity }]
          }
        })
      });

      if (!response.ok) throw new Error(`Storefront API returned ${response.status}`);

      const served = response.headers.get('x-shopify-api-version');
      if (served && served !== apiVersion) {
        console.warn( // eslint-disable-line
          `Buy it now: requested Storefront API ${apiVersion} but the store served ${served}. `
          + 'The pinned version has expired and Shopify is falling forward; bump it in product-form.js.'
        );
      }

      const { data, errors } = await response.json();
      if (errors?.length) throw new Error(errors[0].message);

      const result = data?.cartCreate;
      if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
      if (!result?.cart?.checkoutUrl) throw new Error('No checkoutUrl returned.');

      return result.cart.checkoutUrl;
    }

    /**
     * Fallback path: post the form to /cart/add and let it redirect to checkout.
     * @param {HTMLFormElement} form - The product form.
     */
    static submitToCartAndCheckout(form) {
      const returnTo = document.createElement('input');
      returnTo.type = 'hidden';
      returnTo.name = 'return_to';
      returnTo.value = '/checkout';
      form.appendChild(returnTo);
      form.submit();
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (this.isolatedCheckout) {
        this.handlePreorderCheckout();
        return;
      }

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
